import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress, Paper, Button, IconButton, Tooltip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { exportTableToExcel } from '../utils/excelExport';
import {
  ceilNextInt,
  ceilPercentageLabel,
  ceilRupeeLabel,
  ceilMixedQtyValue,
} from '../utils/ceilDisplay';
import './DailySummaryReport.css';

const DailySummaryReport = () => {
  const tableRef = useRef(null);
  const calendarRef = useRef(null);
  
  const getYesterdayString = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const shiftDate = (dateStr, days) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + days);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const [selectedDate, setSelectedDate] = useState(getYesterdayString());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarViewYear, setCalendarViewYear] = useState(() => parseInt(getYesterdayString().split('-')[0], 10));
  const [calendarViewMonth, setCalendarViewMonth] = useState(() => parseInt(getYesterdayString().split('-')[1], 10) - 1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sync calendar month/year view when date changes via prev/next buttons
  useEffect(() => {
    const parts = selectedDate.split('-').map(Number);
    setCalendarViewYear(parts[0]);
    setCalendarViewMonth(parts[1] - 1);
  }, [selectedDate]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExportExcel = () => {
    exportTableToExcel(tableRef.current, `Daily_Summary_Report_${selectedDate}.xls`);
  };

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://apir4prod.bsynapse.com/ifeccustomguard/dailyproductionreport', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ index: 'ifec', yesterday: selectedDate })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const rowsConfig = [
    { key: 'Total', label: 'Total', type: 'summary', uniquePartsKey: 'total' },
    { key: 'CNC', label: 'CNC/VMC', type: 'summary', uniquePartsKey: 'CNC/VMC' },
    { key: 'Manual', label: 'Manual', type: 'summary', uniquePartsKey: 'Manual' },
    { key: 'Escorts', label: 'Escorts', type: 'brand', uniquePartsKey: 'Escorts' },
    { key: 'Carraro', label: 'Cararo', type: 'brand', uniquePartsKey: 'Carraro' },
    { key: 'ACE', label: 'Ace', type: 'brand', uniquePartsKey: 'ACE' },
    { key: 'Kinetic', label: 'Kinetic', type: 'brand', uniquePartsKey: 'Kinetic' },
    { key: 'Eicher', label: 'Eicher', type: 'brand', uniquePartsKey: 'Eicher' },
    { key: 'Customer', label: 'Customer', type: 'brand', uniquePartsKey: 'Customer' },
    { key: 'Supplier', label: 'Supplier', type: 'brand', uniquePartsKey: 'Supplier' },
    { key: 'Target', label: 'Target', type: 'summary' }
  ];

  const resolveLookupKey = (key) => {
    if (!key || key === 'Customer' || key === 'Supplier' || key === 'Target') return null;
    let lookupKey = key;
    if (key === 'Carraro') lookupKey = 'Carraro';
    if (key === 'ACE') lookupKey = 'ACE';
    if (key === 'Total') lookupKey = 'total';
    if (key === 'CNC' || key === 'Manual') lookupKey = key.toLowerCase();
    return lookupKey;
  };

  const getPresentAbsentParts = (stats, field, rowKey) => {
    if (!stats) return null;
    const lookupKey = resolveLookupKey(rowKey);
    if (lookupKey === null) return null;
    const d = stats[field]?.[lookupKey];
    if (!d) return null;
    return {
      num: ceilNextInt(d.number),
      pct: ceilPercentageLabel(d.percentage),
    };
  };

  const getCellValue = (stats, category, key, row = null) => {
    if (!stats) return '';
    const lookupKey = resolveLookupKey(key);
    if (lookupKey === null) return '';

    const isBrand = !['Total', 'CNC', 'Manual'].includes(key);

    switch (category) {
      case 'wip': {
        const v = stats.wip?.[lookupKey] ?? stats.wip?.brands?.[lookupKey] ?? (isBrand || key === 'Total' ? 0 : '');
        if (v === '') return '';
        return ceilNextInt(v);
      }
      case 'ncr_count': {
        const v = stats.ncr_count?.[lookupKey] ?? (isBrand || key === 'Total' ? 0 : '');
        if (v === '') return '';
        return ceilNextInt(v);
      }
      case 'ncr_qty': {
        const v = stats.ncr_qty?.[lookupKey] ?? (isBrand || key === 'Total' ? 0 : '');
        if (v === '') return '';
        return ceilNextInt(v);
      }
      case 'total_present': {
        const d = stats.total_present?.[lookupKey];
        if (!d) return '';
        const n = ceilNextInt(d.number);
        const pct = ceilPercentageLabel(d.percentage);
        return `${n} (${pct})`;
      }
      case 'total_absent': {
        const d = stats.total_absent?.[lookupKey];
        if (!d) return '';
        const n = ceilNextInt(d.number);
        const pct = ceilPercentageLabel(d.percentage);
        return `${n} (${pct})`;
      }
      case 'ot_hrs': {
        const v = stats.ot_hrs?.[lookupKey];
        if (v === '' || v === null || v === undefined) return '';
        return ceilNextInt(v);
      }
      case 'ot_percentage': {
        const v = stats.ot_percentage?.[lookupKey];
        if (v === '' || v === null || v === undefined) return '';
        return ceilPercentageLabel(v);
      }
      case 'ot_value': {
        const v = stats.ot_value?.[lookupKey];
        if (v === '' || v === null || v === undefined) return '';
        return ceilRupeeLabel(v);
      }
      case 'unique_parts': {
        if (Array.isArray(stats.unique_parts)) return '';
        // Use uniquePartsKey from row config when available for exact API key matching
        const upKey = row?.uniquePartsKey ?? lookupKey;
        if (!upKey) return '';
        const v = stats.unique_parts?.[upKey];
        if (v === null || v === undefined || v === '') return '';
        return ceilNextInt(v);
      }
      case 'scrap_qty': {
        const v = stats.scrap_qty?.[lookupKey] ?? (isBrand || key === 'Total' ? 0 : '');
        if (v === '') return '';
        if (typeof v === 'number' && Number.isFinite(v)) return ceilNextInt(v);
        if (typeof v === 'string') return ceilMixedQtyValue(v);
        return v;
      }
      default:
        return '';
    }
  };

  const displayCell = (value) => {
    if (value === '' || value === null || value === undefined) return '\u2014';
    return value;
  };

  const MetricCell = ({ stats, category, row, className = '' }) => {
    if (
      stats &&
      (category === 'total_present' || category === 'total_absent')
    ) {
      const field = category === 'total_present' ? 'total_present' : 'total_absent';
      const parts = getPresentAbsentParts(stats, field, row.key);
      const empty = parts === null;
      return (
        <td
          className={['numeric-cell', 'numeric-cell--stack-pct', className, empty ? 'cell-empty' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {empty ? (
            '\u2014'
          ) : (
            <span className="metric-cell-stack">
              <span className="metric-value-primary">{parts.num}</span>
              <span className="metric-value-percent">({parts.pct})</span>
            </span>
          )}
        </td>
      );
    }

    if (stats && category === 'ot_percentage') {
      const raw = getCellValue(stats, category, row.key, row);
      const empty = raw === '' || raw === null || raw === undefined;
      return (
        <td
          className={['numeric-cell', 'numeric-cell--pct-col', className, empty ? 'cell-empty' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {empty ? '\u2014' : <span className="metric-value-percent metric-value-percent--solo">{raw}</span>}
        </td>
      );
    }

    const raw = getCellValue(stats, category, row.key, row);
    const empty = raw === '' || raw === null || raw === undefined;
    return (
      <td className={['numeric-cell', className, empty ? 'cell-empty' : ''].filter(Boolean).join(' ')}>
        {displayCell(raw)}
      </td>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, color: 'red' }}>
        <Typography>Error loading data: {error}</Typography>
      </Box>
    );
  }

  // ── Calendar helpers ──────────────────────────────────────────────────────
  const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const DAY_LABELS  = ['Su','Mo','Tu','We','Th','Fr','Sa'];

  const goToPrevCalendarMonth = () => {
    if (calendarViewMonth === 0) { setCalendarViewMonth(11); setCalendarViewYear(y => y - 1); }
    else { setCalendarViewMonth(m => m - 1); }
  };
  const goToNextCalendarMonth = () => {
    if (calendarViewMonth === 11) { setCalendarViewMonth(0); setCalendarViewYear(y => y + 1); }
    else { setCalendarViewMonth(m => m + 1); }
  };
  const selectDay = (year, month, day) => {
    const mm = String(month + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    setSelectedDate(`${year}-${mm}-${dd}`);
    setCalendarOpen(false);
  };

  const renderCalendarPopup = () => {
    const daysInMonth = new Date(calendarViewYear, calendarViewMonth + 1, 0).getDate();
    const firstDayOfWeek = new Date(calendarViewYear, calendarViewMonth, 1).getDay();
    const cells = [...Array(firstDayOfWeek).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) {
      const row = cells.slice(i, i + 7);
      while (row.length < 7) row.push(null);
      rows.push(row);
    }
    return (
      <Box sx={{
        position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 1400,
        backgroundColor: '#fff', borderRadius: '16px',
        boxShadow: '0 16px 48px rgba(102,126,234,0.20), 0 4px 16px rgba(0,0,0,0.10)',
        border: '1px solid #e2e8f0', p: 2.5, width: 310,
      }}>
        {/* Month/Year header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <IconButton size="small" onClick={goToPrevCalendarMonth}
            sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: '4px', '&:hover': { backgroundColor: '#f1f5f9', borderColor: '#667eea' } }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', letterSpacing: '0.3px' }}>
            {MONTH_NAMES[calendarViewMonth]} {calendarViewYear}
          </Typography>
          <IconButton size="small" onClick={goToNextCalendarMonth}
            sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: '4px', '&:hover': { backgroundColor: '#f1f5f9', borderColor: '#667eea' } }}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
        {/* Day-of-week labels */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
          {DAY_LABELS.map(d => (
            <Box key={d} sx={{ textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', py: 0.5 }}>{d}</Box>
          ))}
        </Box>
        {/* Day grid */}
        {rows.map((row, ri) => (
          <Box key={ri} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px', mb: '3px' }}>
            {row.map((day, ci) => {
              if (!day) return <Box key={ci} sx={{ py: '7px' }} />;
              const mm = String(calendarViewMonth + 1).padStart(2, '0');
              const dd = String(day).padStart(2, '0');
              const dateStr = `${calendarViewYear}-${mm}-${dd}`;
              const isSelected = dateStr === selectedDate;
              const isToday    = dateStr === todayStr;
              return (
                <Box key={ci} onClick={() => selectDay(calendarViewYear, calendarViewMonth, day)}
                  sx={{
                    textAlign: 'center', py: '7px', borderRadius: '8px', cursor: 'pointer',
                    fontWeight: isSelected ? 700 : isToday ? 600 : 400,
                    fontSize: '0.85rem',
                    background: isSelected ? 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)' : 'transparent',
                    color: isSelected ? '#fff' : isToday ? '#667eea' : '#334155',
                    border: isToday && !isSelected ? '1.5px solid #667eea' : '1.5px solid transparent',
                    '&:hover': { backgroundColor: isSelected ? undefined : '#f1f5f9' },
                    transition: 'all 0.12s ease',
                  }}>
                  {day}
                </Box>
              );
            })}
          </Box>
        ))}
        {/* Shortcuts */}
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Button size="small" type="button" variant="outlined"
            onClick={() => { setSelectedDate(todayStr); setCalendarOpen(false); }}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderColor: '#667eea', color: '#667eea', borderRadius: '8px', py: 0.4, minWidth: 70, '&:hover': { backgroundColor: '#f0f1ff' } }}>
            Today
          </Button>
          <Button size="small" type="button" variant="outlined"
            onClick={() => { setSelectedDate(getYesterdayString()); setCalendarOpen(false); }}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderColor: '#667eea', color: '#667eea', borderRadius: '8px', py: 0.4, minWidth: 85, '&:hover': { backgroundColor: '#f0f1ff' } }}>
            Yesterday
          </Button>
        </Box>
      </Box>
    );
  };
  // ─────────────────────────────────────────────────────────────────────────

  const ys = data?.yesterdayStats;
  const ms = data?.monthlyStats;

  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const selectedMonthName = selectedDate ? months[parseInt(selectedDate.split('-')[1], 10) - 1] : '';

  return (
    <Box
      sx={{
        height: 'calc(100vh - 64px)',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        backgroundColor: '#fff',
      }}
    >
      <Paper 
        sx={{ 
          p: 1.5, 
          px: 3,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          borderRadius: 0,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', 
          gap: 2, 
          alignItems: 'center',
          flexWrap: 'wrap',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)'
        }} 
        elevation={2}
      >
        {/* Business Date — custom calendar picker (no native popup) */}
        <Box ref={calendarRef} sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mr: 0.5, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Business Date
          </Typography>
          <Tooltip title="Previous day">
            <IconButton size="small" onClick={() => setSelectedDate(shiftDate(selectedDate, -1))}
              sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: '4px', '&:hover': { backgroundColor: '#f1f5f9' } }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open calendar">
            <Box onClick={() => setCalendarOpen(o => !o)}
              sx={{
                px: 1.5, py: 0.6,
                border: `1.5px solid ${calendarOpen ? '#667eea' : '#e2e8f0'}`,
                borderRadius: '8px',
                backgroundColor: calendarOpen ? '#f0f1ff' : '#f8fafc',
                fontWeight: 600, fontSize: '0.88rem', color: '#1e293b',
                display: 'flex', alignItems: 'center', gap: 0.75,
                cursor: 'pointer', userSelect: 'none', minWidth: 152,
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: '#667eea', backgroundColor: '#f0f1ff' }
              }}>
              <CalendarTodayIcon sx={{ fontSize: '0.9rem', color: calendarOpen ? '#667eea' : '#64748b', flexShrink: 0 }} />
              {formatDisplayDate(selectedDate)}
            </Box>
          </Tooltip>
          <Tooltip title="Next day">
            <IconButton size="small" onClick={() => setSelectedDate(shiftDate(selectedDate, 1))}
              sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: '4px', '&:hover': { backgroundColor: '#f1f5f9' } }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {calendarOpen && renderCalendarPopup()}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          type="button"
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportExcel}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            backgroundColor: '#107c41',
            '&:hover': { backgroundColor: '#0b592e' }
          }}
        >
          Export to Excel
        </Button>
      </Paper>
      <Box sx={{ p: 3 }}>
        <div className="daily-summary-container">
          <div className="table-responsive">
            <table ref={tableRef} className="summary-table">
              <thead>
                <tr className="thead-section-row">
                  <th rowSpan={2} className="corner-cell" aria-hidden="true" />
                  <th colSpan={10} className="section-title section-title--business border-right-section">
                    Business day <span className="section-title__meta">({selectedDate})</span>
                  </th>
                  <th colSpan={10} className="section-title section-title--month">
                    Total for the month <span className="section-title__meta">({selectedMonthName})</span>
                  </th>
                </tr>
                <tr className="thead-columns-row">
              <th className="col-head col-head--business">WIP</th>
              <th className="col-head col-head--business">NCR<br />(Count)</th>
              <th className="col-head col-head--business">NCR<br />(Qty)</th>
              <th className="col-head col-head--business">Total<br />Present –<br /><span className="col-head-percent-label">With %</span></th>
              <th className="col-head col-head--business">Total<br />Absent<br /><span className="col-head-percent-label">With %</span></th>
              <th className="col-head col-head--business">OT Hrs</th>
              <th className="col-head col-head--business"><span className="col-head-percent-label">% Of</span><br />OT</th>
              <th className="col-head col-head--business">OT value<br />(approx)</th>
              <th className="col-head col-head--business">Scrap<br />Qty &amp;<br />value</th>
              <th className="col-head col-head--business border-right-section">No. unique<br />parts<br />worked</th>

              <th className="col-head col-head--month">Total<br />WIP</th>
              <th className="col-head col-head--month">Total<br />NCR<br />(Count)</th>
              <th className="col-head col-head--month">Total<br />NCR<br />(Qty)</th>
              <th className="col-head col-head--month">Total<br />Present –<br /><span className="col-head-percent-label">With %</span></th>
              <th className="col-head col-head--month">Total<br />Absent<br /><span className="col-head-percent-label">with %</span></th>
              <th className="col-head col-head--month">Total<br />OT Hrs</th>
              <th className="col-head col-head--month">Total<br /><span className="col-head-percent-label">% Of</span><br />OT</th>
              <th className="col-head col-head--month">Total OT<br />value<br />(approx)</th>
              <th className="col-head col-head--month">Scrap<br />Qty &amp;<br />value</th>
              <th className="col-head col-head--month">Total no.<br />unique<br />parts<br />worked</th>
            </tr>
          </thead>
          <tbody>
            {rowsConfig.map((row, index) => {
              const isTotalRow = row.key === 'Total';
              const rowClass = isTotalRow ? 'data-row data-row--total' : 'data-row';

              return (
                <tr key={index} className={rowClass}>
                  <th scope="row" className="row-header">
                    {row.label === 'Total' ? <strong>{row.label}</strong> : row.label}
                  </th>

                  <MetricCell stats={ys} category="wip" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="ncr_count" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="ncr_qty" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="total_present" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="total_absent" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="ot_hrs" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="ot_percentage" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="ot_value" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="scrap_qty" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="unique_parts" row={row} className="cell-business-day border-right-section" />

                  <MetricCell stats={ms} category="wip" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="ncr_count" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="ncr_qty" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="total_present" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="total_absent" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="ot_hrs" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="ot_percentage" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="ot_value" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="scrap_qty" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="unique_parts" row={row} className="cell-month" />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
      </Box>
    </Box>
  );
};

export default DailySummaryReport;
